import { useRouterState } from "@tanstack/react-router";

import type { LabelKey } from "../domain/labels";
import { routes, type RouteEntry } from "../routes/routeTable";
import { useLabels } from "./useLabels";

/** Route paths whose title is a dictionary noun. */
const TITLE_LABEL_KEYS: Record<string, LabelKey> = {
  "/sites": "sites",
  "/products": "products",
  "/lane-capacities": "laneRates",
  "/modes": "modes",
  "/carriers": "carriers",
  "/calendars": "closures",
  "/priority-policies": "priorityPolicies",
  "/demand/transfer-requests": "transferRequests",
  "/demand/booked-loads": "bookedLoads",
  "/runs": "planningRuns",
};

const toPattern = (path: string) =>
  new RegExp(`^${path.replace(/:[^/]+/g, "[^/]+").replace(/\//g, "\\/")}$`);

/** The route table entry a pathname resolves to, or the catch-all. */
export function matchRoute(pathname: string): RouteEntry | undefined {
  const exact = routes.find((r) => r.path === pathname);
  if (exact) return exact;
  const param = routes.find((r) => r.path.includes(":") && toPattern(r.path).test(pathname));
  return param ?? routes.find((r) => r.path === "*");
}

/** The page title the shell shows, through the dictionary where the title is a noun. */
export function useRouteTitle(): string {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const labels = useLabels();
  const entry = matchRoute(pathname);
  if (!entry) return "";
  const key = TITLE_LABEL_KEYS[entry.path];
  return key ? labels[key] : entry.title;
}

/** The pathname, for active-item marking. */
export function useCurrentPath(): string {
  return useRouterState({ select: (s) => s.location.pathname });
}
